import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { blueprintsApi, type FaseBlueprint } from '../api/blueprints.js';
import { BotaoComBloqueio } from '../componentes/transversais/BotaoComBloqueio.js';
import { mensagemDeErro } from '../mensagens/mensagens.js';
import { ErroRequisicao } from '../api/cliente.js';

function checklistPublicacao(fases: FaseBlueprint[]): string[] {
  const faltando: string[] = [];
  for (const fase of fases) {
    if (fase.skills.length === 0) faltando.push(`Fase "${fase.nome}": nenhuma skill exigida`);
    if (!fase.criteriosGate.some((c) => c.obrigatorio)) faltando.push(`Fase "${fase.nome}": nenhum critério de gate obrigatório`);
  }
  return faltando;
}

function CartaoFase({ fase }: { fase: FaseBlueprint }) {
  return (
    <div className="w-64 flex-shrink-0 rounded-lg border border-borda bg-superficie p-3">
      <p className="text-sm font-medium text-ink">
        {fase.ordem}. {fase.nome}
      </p>
      {fase.duracaoEstimadaDias !== null && <p className="fonte-mono text-xs text-grafite">{fase.duracaoEstimadaDias}d estimados</p>}

      <p className="mt-2 text-xs font-semibold text-grafite">Skills</p>
      {fase.skills.length === 0 ? (
        <p className="text-xs text-status-vermelho">nenhuma — publicação bloqueada</p>
      ) : (
        <ul className="flex flex-wrap gap-1">
          {fase.skills.map((s) => (
            <li key={s} className="fonte-mono rounded bg-aco/10 px-1.5 py-0.5 text-[11px] text-aco-700">
              {s}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-xs font-semibold text-grafite">Critérios de gate</p>
      {!fase.criteriosGate.some((c) => c.obrigatorio) ? (
        <p className="text-xs text-status-vermelho">nenhum obrigatório — publicação bloqueada</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {fase.criteriosGate.map((c, i) => (
            <li key={i} className="text-xs text-ink">
              {c.obrigatorio ? '●' : '○'} {c.descricao}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// SPEC-UX §4.3 — editor em rascunho: fases como colunas ordenáveis; cada
// fase valida inline skills (R1.6) e gate mínimo (R1.2); publicação
// bloqueada com checklist do que falta. Publicado é somente leitura com
// selo de versão. Reordenar fases por drag-and-drop fica para uma fatia
// futura (a validação inline e o bloqueio de publicação, que são o
// comportamento central da spec aqui, já funcionam).
export function BlueprintDetalhe() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [idComparar, setIdComparar] = useState('');

  const { data: blueprint } = useQuery({ queryKey: ['blueprint', id], queryFn: () => blueprintsApi.buscar(id!), enabled: !!id });
  const { data: diff } = useQuery({
    queryKey: ['blueprint', 'diff', id, idComparar],
    queryFn: () => blueprintsApi.diff(id!, idComparar),
    enabled: !!id && idComparar.trim().length > 0,
  });

  const publicar = useMutation({
    mutationFn: () => blueprintsApi.publicar(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blueprint', id] }),
    onError: (e) => setErro(e instanceof ErroRequisicao ? mensagemDeErro(e.erro.codigo) : 'Não foi possível publicar.'),
  });

  if (!blueprint) return <div className="p-8 text-sm text-grafite">Carregando…</div>;

  const faltando = checklistPublicacao(blueprint.fases);
  const podePublicar = blueprint.estado === 'Rascunho' && faltando.length === 0;

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">{blueprint.nome}</h1>
          <p className="fonte-mono text-xs text-grafite">
            v{blueprint.versao} · {blueprint.estado}
          </p>
        </div>
        {blueprint.estado === 'Rascunho' && (
          <BotaoComBloqueio
            disponivel={podePublicar}
            motivo={faltando.length > 0 ? faltando[0] : undefined}
            onClick={() => {
              setErro(null);
              publicar.mutate();
            }}
          >
            Publicar
          </BotaoComBloqueio>
        )}
      </div>

      {erro && <p className="text-sm text-status-vermelho">{erro}</p>}

      {blueprint.estado === 'Rascunho' && faltando.length > 0 && (
        <div className="rounded border border-status-vermelho/30 bg-status-vermelho/5 p-3">
          <p className="mb-1 text-xs font-semibold text-status-vermelho">Checklist antes de publicar</p>
          <ul className="list-inside list-disc text-xs text-status-vermelho">
            {faltando.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-grafite">Fases</h2>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {blueprint.fases.map((fase) => (
            <CartaoFase key={fase.ordem} fase={fase} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-grafite">Diff com outra versão</h2>
        <input
          placeholder="ID do blueprint para comparar"
          value={idComparar}
          onChange={(e) => setIdComparar(e.target.value)}
          className="w-full max-w-sm rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
        />
        {diff && (
          <ul className="mt-3 flex flex-col gap-1">
            {diff.fases.map((f) => (
              <li key={f.nome} className="flex items-center gap-2 text-sm">
                <span
                  className={`fonte-mono w-20 rounded px-1.5 py-0.5 text-center text-[11px] ${
                    f.status === 'adicionada'
                      ? 'bg-status-verde/10 text-status-verde'
                      : f.status === 'removida'
                        ? 'bg-status-vermelho/10 text-status-vermelho'
                        : f.status === 'alterada'
                          ? 'bg-status-ambar/10 text-status-ambar'
                          : 'bg-borda/40 text-grafite'
                  }`}
                >
                  {f.status}
                </span>
                <span className="text-ink">{f.nome}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
